import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { FileText, Download } from "lucide-react";
import { analysesApi, projectsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  Document, Packer, Paragraph, HeadingLevel, TextRun, AlignmentType,
  Table as DocTable, TableRow, TableCell, WidthType, BorderStyle,
} from "docx";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const PRESETS = [
  { v: "parliamentary", l: "Rapport Parlementaire", desc: "15 pages max · Pédagogique, synthétique" },
  { v: "technical", l: "Rapport Technique FMI/BM", desc: "40-60 pages · Exhaustif, données brutes" },
  { v: "citizen", l: "Rapport Citoyen", desc: "10 pages · Accessible, illustré" },
  { v: "vitaliste", l: "Rapport Vitaliste", desc: "25 pages · Formules vitalistes + applications" },
  { v: "convention", l: "Rapport Convention", desc: "20-30 pages · Clauses analysées" },
];

export default function Reports() {
  const { projectId } = useParams();
  const [preset, setPreset] = useState("parliamentary");
  const [data, setData] = useState(null);
  const [project, setProject] = useState(null);
  const reportRef = useRef();
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    Promise.all([analysesApi.summary(projectId), projectsApi.get(projectId)]).then(([s, p]) => {
      setData(s); setProject(p);
    });
  }, [projectId]);

  const exportPdf = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2, backgroundColor: "#ffffff", useCORS: true, logging: false,
      });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(img, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(img, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      const name = `DEBT-ANALYZER-PRO-${project.country}-${project.period_start}-${project.period_end}-${preset}.pdf`;
      pdf.save(name);
      toast.success("Rapport exporté");
    } catch (e) {
      toast.error("Erreur export : " + e.message);
    } finally { setExporting(false); }
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `debt-analyzer-${project.country}-${Date.now()}.json`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("Export JSON téléchargé");
  };

  const exportWord = async () => {
    try {
      const v = data.macro.viabilite || {};
      const vit = data.vitaliste || {};
      const P = (text, opts = {}) => new Paragraph({
        children: [new TextRun({ text, ...opts })],
        spacing: { after: 120 },
        ...opts,
      });
      const H = (text, level) => new Paragraph({
        heading: level, children: [new TextRun({ text, bold: true })],
        spacing: { before: 240, after: 120 },
      });
      const row = (label, val) => new TableRow({
        children: [
          new TableCell({ width: { size: 60, type: WidthType.PERCENTAGE }, children: [P(label)] }),
          new TableCell({ width: { size: 40, type: WidthType.PERCENTAGE }, children: [P(val == null ? "—" : String(val))] }),
        ],
      });
      const kpiTable = (rows) => new DocTable({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: rows.map(([l, v]) => row(l, v)),
      });

      const doc = new Document({
        styles: { default: { document: { run: { font: "Calibri" } } } },
        sections: [{
          children: [
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: [new TextRun({ text: "DEBT-ANALYZER PRO", bold: true, size: 18 })],
            }),
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: [new TextRun({ text: PRESETS.find((p) => p.v === preset)?.l || "Rapport", bold: true, size: 36 })],
              spacing: { after: 240 },
            }),
            P(`${project.name} · ${project.country} · ${project.period_start}–${project.period_end}`),
            P(`Date : ${new Date().toLocaleDateString("fr-FR")}`),

            H("Synthèse exécutive", HeadingLevel.HEADING_1),
            kpiTable([
              ["Dette / PIB (%)", v.debt_gdp_ratio?.toFixed?.(1)],
              ["Service / Recettes (%)", v.service_revenue_ratio?.toFixed?.(1)],
              ["TAVD — V5 (%)", vit.V5?.toFixed?.(1)],
              ["D2 — jours hypothéqués", vit.D2?.toFixed?.(0)],
              ["V6 — enfant endetté (jours)", vit.V6?.toFixed?.(0)],
            ]),

            H("Analyse vitaliste", HeadingLevel.HEADING_1),
            ...(vit.interpretations || []).map((m) => P(`« ${m} »`, { italics: true })),

            H("Risques & recommandations", HeadingLevel.HEADING_1),
            ...((data.aggregated?.key_risks || []).slice(0, 10).map((r) => P(`• ${r}`))),
            ...((data.aggregated?.key_recommendations || []).slice(0, 10).map((r) => P(`✓ ${r}`))),

            new Paragraph({
              spacing: { before: 480 }, alignment: AlignmentType.CENTER,
              children: [new TextRun({
                text: "Méthodologie : A. ELY Mustapha — Théorie Nouvelle des Finances Publiques (Bookedition, Paris). Document pédagogique sans valeur juridictionnelle.",
                italics: true, size: 18,
              })],
            }),
          ],
        }],
      });
      const blob = await Packer.toBlob(doc);
      saveAs(blob, `DEBT-ANALYZER-${project.country}-${preset}.docx`);
      toast.success("Rapport Word exporté");
    } catch (e) {
      toast.error("Erreur Word : " + e.message);
    }
  };

  const exportExcel = () => {
    try {
      const wb = XLSX.utils.book_new();
      const v = data.macro.viabilite || {};
      const vit = data.vitaliste || {};
      const macroRows = Object.entries(v).map(([k, val]) => ({ Indicateur: k, Valeur: val }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(macroRows), "Macro");
      const vitRows = [
        { Code: "D1", Libellé: "Dette / contribuable", Valeur: vit.D1, Unité: project.currency },
        { Code: "D2", Libellé: "Jours hypothéqués", Valeur: vit.D2, Unité: "j" },
        { Code: "D4", Libellé: "Carrière aliénée", Valeur: vit.D4, Unité: "%" },
        { Code: "D5", Libellé: "Service en jours/an", Valeur: vit.D5, Unité: "j" },
        { Code: "V1", Libellé: "Jours fiscaux", Valeur: vit.V1, Unité: "j" },
        { Code: "V5", Libellé: "TAVD", Valeur: vit.V5, Unité: "%" },
        { Code: "V6", Libellé: "Enfant endetté", Valeur: vit.V6, Unité: "j" },
        { Code: "IPSD", Libellé: "Indice Pression Sociale Dette", Valeur: vit.IPSD, Unité: "%" },
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(vitRows), "Vitaliste");
      const clauseRows = (data.clauses?.clauses || []).map((c) => ({
        clause_id: c.clause_id, creditor: c.creditor, type: c.clause_type,
        amount: c.amount, currency: c.currency, interest_rate: c.interest_rate,
        maturity_years: c.maturity_years, grant_element_pct: c.grant_element_pct,
        vitalist_cost_years: c.vitalist_cost_years, risk_level: c.risk_level,
      }));
      if (clauseRows.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(clauseRows), "Clauses");
      const alertRows = (data.clauses?.alerts || []).map((a) => ({ clause_id: a.clause_id, type: a.type, severity: a.severity, msg: a.msg }));
      if (alertRows.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(alertRows), "Alertes");
      const oddRows = Object.entries(data.odd?.matrix || {}).map(([k, v]) => ({ ODD: k, impact: v.color, occurrences: v.occurrences }));
      if (oddRows.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(oddRows), "ODD");
      const sourcesRows = (data.sources || []).map((s) => ({ filename: s.filename, doc_type: s.doc_type, year: s.year, institution: s.institution }));
      if (sourcesRows.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sourcesRows), "Sources");
      XLSX.writeFile(wb, `DEBT-ANALYZER-${project.country}-${preset}.xlsx`);
      toast.success("Export Excel téléchargé");
    } catch (e) {
      toast.error("Erreur Excel : " + e.message);
    }
  };

  if (!data || !project) return <div className="p-10 label-mono text-xs text-muted-foreground">Chargement…</div>;

  return (
    <div className="px-6 lg:px-10 py-8 max-w-7xl mx-auto w-full" data-testid="reports-page">
      <div className="label-mono text-[10px] text-muted-foreground">ESPACE 4</div>
      <h1 className="mt-1 font-serif font-bold text-4xl">Rapports exportables</h1>
      <p className="mt-2 text-muted-foreground text-sm max-w-2xl">
        Choisissez un preset et exportez le rapport en PDF haute résolution. Export JSON pour intégrations.
      </p>

      <div className="mt-6 flex flex-wrap items-end gap-4 border border-border bg-card rounded-sm p-5">
        <div className="flex-1 min-w-[260px]">
          <div className="label-mono text-[10px] text-muted-foreground">PRESET</div>
          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger className="mt-2" data-testid="preset-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PRESETS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="mt-2 text-xs text-muted-foreground">
            {PRESETS.find((p) => p.v === preset)?.desc}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={exportJson} data-testid="export-json-btn">
            <Download size={14} className="mr-2" /> JSON
          </Button>
          <Button variant="outline" onClick={exportExcel} data-testid="export-excel-btn">
            <Download size={14} className="mr-2" /> Excel
          </Button>
          <Button variant="outline" onClick={exportWord} data-testid="export-word-btn">
            <Download size={14} className="mr-2" /> Word
          </Button>
          <Button onClick={exportPdf} disabled={exporting} className="bg-marine hover:bg-marine/90 text-white"
                  data-testid="export-pdf-btn">
            <FileText size={14} className="mr-2" /> {exporting ? "Export…" : "Exporter PDF"}
          </Button>
        </div>
      </div>

      {/* Report preview — printable */}
      <div ref={reportRef} className="mt-8 bg-white text-black border border-border rounded-sm p-10 print-area"
           data-testid="report-preview" style={{ minHeight: 1200 }}>
        <ReportCover project={project} preset={preset} />
        <ReportSummary data={data} project={project} />
        <ReportMacro data={data} />
        <ReportVitaliste data={data} project={project} />
        <ReportClauses data={data} />
        <ReportRecommendations data={data} />
        <div className="mt-16 pt-6 border-t border-gray-300 text-[9px] text-gray-600 text-center">
          DEBT-ANALYZER PRO · Méthodologie vitaliste : Ahmed ELY Mustapha — Théorie Nouvelle des Finances Publiques (Bookedition, Paris)
          <br />Document pédagogique — n'a pas de valeur juridictionnelle.
        </div>
      </div>
    </div>
  );
}

function ReportCover({ project, preset }) {
  const title = PRESETS.find((p) => p.v === preset)?.l || "Rapport";
  return (
    <div className="border-b-2 border-gray-900 pb-8 mb-10">
      <div className="flex justify-between items-start">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-gray-600">DEBT-ANALYZER PRO</div>
          <h1 className="mt-2 font-serif font-black text-4xl text-gray-900">{title}</h1>
          <h2 className="mt-1 font-serif text-2xl text-gray-700">{project.name}</h2>
        </div>
        <div className="text-right text-xs text-gray-600">
          <div><strong>Pays :</strong> {project.country}</div>
          <div><strong>Période :</strong> {project.period_start}–{project.period_end}</div>
          <div><strong>Devise :</strong> {project.currency}</div>
          <div><strong>Date :</strong> {new Date().toLocaleDateString("fr-FR")}</div>
        </div>
      </div>
    </div>
  );
}

function ReportSummary({ data, project }) {
  const v = data.macro.viabilite || {};
  const vit = data.vitaliste || {};
  return (
    <section className="mb-10">
      <h3 className="font-serif font-bold text-2xl border-b border-gray-400 pb-2 text-gray-900">Synthèse exécutive</h3>
      <ul className="mt-4 space-y-1.5 text-sm text-gray-800">
        <li>• Dette / PIB : <strong className="font-mono">{fmtPct(v.debt_gdp_ratio)}</strong> (niveau {v.debt_gdp_band?.toUpperCase()})</li>
        <li>• Service / Recettes : <strong className="font-mono">{fmtPct(v.service_revenue_ratio)}</strong> (niveau {v.service_revenue_band?.toUpperCase()})</li>
        <li>• TAVD (V5) : <strong className="font-mono">{fmtPct(vit.V5)}</strong> — niveau vitaliste <strong>{vit.tavd_band?.toUpperCase()}</strong></li>
        <li>• D2 (jours/contribuable) : <strong className="font-mono">{fmt(vit.D2)}</strong> jours hypothéqués</li>
        <li>• V6 (enfant endetté) : <strong className="font-mono">{fmt(vit.V6)}</strong> jours de vie dès la naissance</li>
      </ul>
    </section>
  );
}

function ReportMacro({ data }) {
  const v = data.macro.viabilite || {};
  const c = data.macro.composition || {};
  return (
    <section className="mb-10">
      <h3 className="font-serif font-bold text-2xl border-b border-gray-400 pb-2 text-gray-900">1. Analyse macro-financière</h3>
      <table className="w-full text-xs mt-4">
        <tbody>
          {Object.entries(v).map(([k, val]) => (
            <tr key={k} className="border-b border-gray-200">
              <td className="py-1 text-gray-700">{k}</td>
              <td className="py-1 text-right font-mono">{val == null ? "—" : typeof val === "number" ? val.toFixed(2) : val}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {Object.keys(c).length > 0 && (
        <div className="mt-4">
          <div className="text-xs text-gray-700 font-bold">Composition de la dette</div>
          <table className="w-full text-xs mt-2">
            <tbody>
              {Object.entries(c).map(([k, v]) => (
                <tr key={k} className="border-b border-gray-200">
                  <td className="py-1">{k}</td>
                  <td className="py-1 text-right font-mono">{v.amount ? v.amount.toLocaleString("fr-FR") : "—"}</td>
                  <td className="py-1 text-right font-mono">{v.pct != null ? v.pct + "%" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ReportVitaliste({ data, project }) {
  const v = data.vitaliste || {};
  return (
    <section className="mb-10">
      <h3 className="font-serif font-bold text-2xl border-b border-gray-400 pb-2 text-gray-900">2. Analyse vitaliste</h3>
      <p className="mt-2 text-xs italic text-gray-700">
        Méthodologie : Théorie Nouvelle des Finances Publiques — Ahmed ELY Mustapha (Bookedition, Paris)
      </p>
      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
        {[
          ["D1 — Dette / contribuable", v.D1, project.currency],
          ["D2 — Jours hypothéqués", v.D2, "j"],
          ["D4 — Carrière aliénée", v.D4, "%"],
          ["D5 — Service en jours/an", v.D5, "j"],
          ["V1 — Jours fiscaux", v.V1, "j"],
          ["V5 — TAVD", v.V5, "%"],
          ["V6 — Enfant endetté", v.V6, "j"],
          ["IPSD", v.IPSD, "%"],
        ].map(([l, val, u]) => (
          <div key={l} className="border border-gray-300 p-2">
            <div className="text-[9px] uppercase text-gray-600">{l}</div>
            <div className="font-mono text-sm mt-0.5">{val == null ? "—" : val.toFixed(1)} {u}</div>
          </div>
        ))}
      </div>
      {v.interpretations?.length > 0 && (
        <div className="mt-4 border-l-2 border-gray-800 pl-4 text-xs italic text-gray-800 space-y-1.5">
          {v.interpretations.map((m, i) => <div key={i}>« {m} »</div>)}
        </div>
      )}
    </section>
  );
}

function ReportClauses({ data }) {
  const c = data.clauses || {};
  if (!c.count) return null;
  return (
    <section className="mb-10">
      <h3 className="font-serif font-bold text-2xl border-b border-gray-400 pb-2 text-gray-900">3. Conventions de prêts</h3>
      <p className="mt-2 text-xs text-gray-700">
        {c.count} clause(s) · {c.alerts?.length || 0} alerte(s) · {c.critical_count} critique(s)
      </p>
      <table className="w-full text-[10px] mt-3">
        <thead className="bg-gray-100 text-gray-700">
          <tr>
            <th className="text-left p-1">Créancier</th><th className="text-left p-1">Type</th>
            <th className="text-right p-1">Montant</th><th className="text-right p-1">Taux</th>
            <th className="text-right p-1">Mat.</th><th className="text-right p-1">Elem-don</th>
            <th className="text-left p-1">Risque</th>
          </tr>
        </thead>
        <tbody>
          {(c.clauses || []).slice(0, 30).map((x, i) => (
            <tr key={i} className="border-b border-gray-200">
              <td className="p-1">{x.creditor || "—"}</td>
              <td className="p-1">{x.clause_type}</td>
              <td className="p-1 text-right font-mono">{fmt(x.amount)}</td>
              <td className="p-1 text-right font-mono">{x.interest_rate}%</td>
              <td className="p-1 text-right font-mono">{x.maturity_years}y</td>
              <td className="p-1 text-right font-mono">{x.grant_element_pct ?? "—"}</td>
              <td className="p-1">{x.risk_level}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function ReportRecommendations({ data }) {
  const risks = data.aggregated?.key_risks || [];
  const recs = data.aggregated?.key_recommendations || [];
  return (
    <section className="mb-10">
      <h3 className="font-serif font-bold text-2xl border-b border-gray-400 pb-2 text-gray-900">4. Risques & Recommandations</h3>
      {risks.length > 0 && <div className="mt-4">
        <div className="text-xs font-bold text-gray-700">Risques clés</div>
        <ul className="mt-1 text-xs text-gray-800 space-y-1 list-disc pl-4">
          {risks.slice(0, 10).map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      </div>}
      {recs.length > 0 && <div className="mt-4">
        <div className="text-xs font-bold text-gray-700">Recommandations</div>
        <ul className="mt-1 text-xs text-gray-800 space-y-1 list-disc pl-4">
          {recs.slice(0, 10).map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      </div>}
    </section>
  );
}

function fmt(n) { return n == null ? "—" : Number(n).toLocaleString("fr-FR"); }
function fmtPct(n) { return n == null ? "—" : Number(n).toFixed(1) + "%"; }
